import { useEffect } from 'react';
import { api } from '@/shared/api/client';
import { useAuth } from '@/app/providers/AuthProvider';
import { normalizePlan } from '@/shared/format/plan';

interface TenantInfo {
  Id: string;
  Name: string;
  Plan: number | string;
  PlanExpiresOn?: string | null;
  TrialEndsOn?: string | null;
  SubscriptionStatus?: string | null;
}

const SERVER_NAMES = ['FreeTrial', 'Starter', 'Gold', 'Premium', 'Enterprise'] as const;

/**
 * Backend `/api/tenant/my`-ээс tenant info-г уншиж localStorage cache-ыг шинэчилнэ.
 * 5 минут стале бол fetch хийнэ.
 */
export function usePlanRefresh(): void {
  const { snapshot, refresh } = useAuth();

  useEffect(() => {
    if (!snapshot.tenantId || !snapshot.token) return;
    const cached = (() => {
      try {
        const raw = localStorage.getItem('tsetsu.tenant.json');
        return raw ? (JSON.parse(raw) as { _ts?: number }) : null;
      } catch {
        return null;
      }
    })();
    const stale = !cached || Date.now() - (cached._ts ?? 0) > 5 * 60_000;
    if (!stale) return;

    api
      .get<TenantInfo[]>('/api/tenant/my')
      .then((list) => {
        const t = list.find((x) => x.Id === snapshot.tenantId) ?? list[0];
        if (!t) return;
        const planEnumName =
          typeof t.Plan === 'number'
            ? (SERVER_NAMES[t.Plan] ?? 'FreeTrial')
            : String(t.Plan ?? 'FreeTrial');
        localStorage.setItem('tsetsu.plan', planEnumName);
        localStorage.setItem(
          'tsetsu.tenant.json',
          JSON.stringify({
            Id: t.Id,
            Name: t.Name,
            Plan: planEnumName,
            PlanExpiresOn: t.PlanExpiresOn ?? null,
            TrialEndsOn: t.TrialEndsOn ?? null,
            SubscriptionStatus: t.SubscriptionStatus ?? null,
            _ts: Date.now(),
          }),
        );
        refresh();
      })
      .catch(() => {
        /* graceful */
      });
    // normalizePlan-ыг exhaustive deps-ээс хасах учиргүй; зөвхөн lookup pure func.
    void normalizePlan;
  }, [snapshot.tenantId, snapshot.token, refresh]);
}
